import { createReducer, on } from '@ngrx/store';
import { Departures } from 'app/core/enums/common.enum';
import * as TotalGrossActions from '../total-gross/total-gross.actions';
import * as ConcessionSaleActions from './concession-sales.actions';
import { ConcessionSale } from './concession-sales.models';

// ----------------------------------------------------------------------
// @ States
// ----------------------------------------------------------------------

export interface ConcessionSalesState {
    sales: ConcessionSale[];
    sale: ConcessionSale | null;
    isLoading: boolean;
    error: string | null;
}

// ----------------------------------------------------------------------
// @ Initial States
// ----------------------------------------------------------------------

export const initialConcessionSalesState: ConcessionSalesState = {
    sales: [],
    sale: null,
    isLoading: false,
    error: null,
};

// ----------------------------------------------------------------------
// @ Reducers
// ----------------------------------------------------------------------

// All Concession Sales Reducer
export const ConcessionSalesReducer = createReducer(
    initialConcessionSalesState,

    // On Load Concession Sales
    on(ConcessionSaleActions.loadConcessionSales, (state) => ({
        ...state,
        isLoading: true,
    })),
    on(ConcessionSaleActions.loadConcessionSalesSuccess, (state, { data }) => ({
        ...state,
        isLoading: false,
        sales: data,
    })),
    on(
        ConcessionSaleActions.loadConcessionSalesFailure,
        (state, { error }) => ({
            ...state,
            isLoading: false,
            error: error,
        })
    ),

    // On Clear
    on(ConcessionSaleActions.clearConcessionSales, (state) => ({
        ...state,
        sales: [],
        sale: null,
        isLoading: false,
        error: null,
    })),

    // On Load Single ConcessionSale
    on(ConcessionSaleActions.loadConcessionSale, (state) => ({
        ...state,
        isLoading: true,
    })),
    on(ConcessionSaleActions.loadConcessionSaleSuccess, (state, { data }) => ({
        ...state,
        isLoading: false,
        sale: data,
    })),
    on(ConcessionSaleActions.loadConcessionSaleFailure, (state, { error }) => ({
        ...state,
        isLoading: false,
        error: error,
    })),

    // On Create
    on(ConcessionSaleActions.createConcessionSale, (state) => ({
        ...state,
        isLoading: true,
    })),
    on(
        ConcessionSaleActions.createConcessionSaleSuccess,
        (state, { sale }) => ({
            ...state,
            isLoading: false,
            sales: ((): ConcessionSale[] => {
                const sales = state.sales || [];
                let updateSales = [];
                sales.some((s) => s.location.id === sale.location.id)
                    ? (updateSales = sales.map((_sale: ConcessionSale) =>
                          _sale.location.id === sale.location.id
                              ? {
                                    // Spread the old sale to keep old cash
                                    // and credit card
                                    ..._sale,
                                    children: [
                                        ..._sale.children,
                                        ...sale.children,
                                    ],
                                    totals: sale.totals,
                                }
                              : _sale
                      ))
                    : (updateSales = [...sales, sale]);

                return updateSales;
            })(),
        })
    ),
    on(
        ConcessionSaleActions.createConcessionSaleFailure,
        (state, { error }) => ({
            ...state,
            isLoading: false,
            error: error,
        })
    ),

    // On Update
    on(ConcessionSaleActions.updateConcessionSale, (state) => ({
        ...state,
        isLoading: true,
    })),
    on(
        ConcessionSaleActions.updateConcessionSaleSuccess,
        (state, { data }) => ({
            ...state,
            isLoading: false,
            sales: state.sales.map((s) =>
                s.location.id === data.location.id
                    ? {
                          ...s,
                          children: s.children.map((child) =>
                              child.saleId === data.children[0].saleId
                                  ? data.children[0]
                                  : child
                          ),
                          totals: data.totals,
                      }
                    : s
            ),
        })
    ),
    on(
        ConcessionSaleActions.updateConcessionSaleFailure,
        (state, { error }) => ({
            ...state,
            isLoading: false,
            error: error,
        })
    ),

    // On Update Location Gross
    on(
        TotalGrossActions.updateTotalGrossSuccess,
        (state, { data, departure }) => ({
            ...state,
            isLoading: false,
            sales: state.sales.map((s) =>
                s.location.id === data.location_id &&
                departure === Departures.CONCESSION
                    ? {
                          ...s,
                          cash: data.cash,
                          credit_card: data.credit_card,
                          total_money: data.total_gross,
                          totals_local: {
                              cash: data.total_local.cash,
                              credit_card: data.total_local.credit_card,
                              count: s.totals_local.count,
                              amount: data.total_gross,
                          },
                      }
                    : s
            ),
        })
    ),

    // On Delete
    on(ConcessionSaleActions.deleteConcessionSale, (state) => ({
        ...state,
        isLoading: true,
    })),
    on(
        ConcessionSaleActions.deleteConcessionSaleSuccess,
        (state, { deletedSales, remainingSales }) => ({
            ...state,
            isLoading: false,
            sales: (() => {
                if (remainingSales.length === 0) {
                    return state.sales.filter(
                        (sales: ConcessionSale) =>
                            !sales.children.some(
                                (child) => child.saleId === deletedSales[0]
                            )
                    );
                } else {
                    console.warn(
                        'Remaining sales after deletion:',
                        remainingSales
                    );
                }
            })(),
        })
    ),
    on(
        ConcessionSaleActions.deleteConcessionSaleFailure,
        (state, { error }) => ({
            ...state,
            isLoading: false,
            error: error,
        })
    ),

    // On Delete Sale Item
    on(ConcessionSaleActions.deleteConcessionSaleItem, (state) => ({
        ...state,
        isLoading: true,
    })),
    on(
        ConcessionSaleActions.deleteConcessionSaleItemSuccess,
        (state, { deletedSaleItem, totals }) => ({
            ...state,
            isLoading: false,
            sales: state.sales.map((_sale: ConcessionSale) =>
                _sale.children.some((child) => child.saleId === deletedSaleItem)
                    ? {
                          ..._sale,
                          children: _sale.children.filter(
                              (child) => child.saleId !== deletedSaleItem
                          ),
                          totals: totals,
                      }
                    : _sale
            ),
        })
    ),
    on(
        ConcessionSaleActions.deleteConcessionSaleItemFailure,
        (state, { error }) => ({
            ...state,
            isLoading: false,
            error: error,
        })
    )
);
